import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useSelector } from "react-redux";
import {
  useCreateOrderMarketMutation,
  useCreateOrderMutation,
  useModifyPositionsMutation,
} from "../../../redux/exchangeUniApi";
import toast from "react-hot-toast";
import QuantityInput from "./QuntityInput";
import { formatDecimal } from "../../../util/decimalFormatter";
import { useCurrency } from "../../../context/CurrencyContext";
import {
  validateSLTP,
  isValidNumericInput,
} from "../../validations/slTpValidator";

const TradeBox = ({
  isOrderBoxShow,
  setIsOrderBoxShow,
  existingOrder = null,
  getExistingOrder,
  leverage,
  shouldCloseModal,
  onModalClosed,
}) => {
  const isEditing = !!existingOrder?.id;
  const userData = useSelector((state) => state.user.userData);
  const watchlist = useSelector((state) => state.watchlist.items);
  const stepSize = useSelector((state) => state.watchlist.stepSize);
  const priceStep = useSelector((state) => state.watchlist.priceStep);
  const priceInitializedRef = useRef(false);
  const [priceError, setPriceError] = useState("");
  const [slError, setSlError] = useState("");
  const [tpError, setTpError] = useState("");
  const [side, setSide] = useState("BUY");
  const [orderType, setOrderType] = useState("market");
  const [quantity, setQuantity] = useState("");
  const [price, setPrice] = useState("");
  const [sl, setSL] = useState("");
  const [tp, setTP] = useState("");
  const [enableSL, setEnableSL] = useState(false);
  const [enableTP, setEnableTP] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [submitLock, setSubmitLock] = useState(false);
  const [estimatedLoss, setEstimatedLoss] = useState(null);
  const [estimatedProfit, setEstimatedProfit] = useState(null);
  const [total, setTotal] = useState(0);
  const [net, setNet] = useState(0);
  const [createMarketOrder] = useCreateOrderMarketMutation();
  const [createOrder] = useCreateOrderMutation();
  const [modifyOrder] = useModifyPositionsMutation();
  const selectedSymbol = useSelector((state) => state.watchlist.selectedSymbol);
  const [minQtyError, setMinQtyError] = useState("");
  const selectedSegment = useSelector(
    (state) => state.watchlist.selectedSegment
  );
  const selectedContractSize = useSelector(
    (state) => state.watchlist.contractSize
  );
  const buyCommission = useSelector((state) => state.watchlist.buyCommission);
  const sellCommission = useSelector((state) => state.watchlist.sellCommission);
  const commissionType = useSelector((state) => state.watchlist.commissionType);
  const { currency, setCurrency } = useCurrency();
  const livePrices = useSelector((state) => state.market.prices);

  const resetState = () => {
    setSide("BUY");
    setOrderType("market");
    setQuantity("");
    setPrice("");
    setSL("");
    setTP("");
    setEnableSL(false);
    setEnableTP(false);
    setTotal(0);
    setNet(0);
    setPriceError("");
    setSlError("");
    setTpError("");
    setHasError("");
  };

  const resetDefault = () => {
    setQuantity("");
    setSL("");
    setTP("");
    setEnableSL(false);
    setEnableTP(false);
    setTotal(0);
    setNet(0);
    setPriceError("");
    setSlError("");
    setTpError("");
    setHasError("");
    setSubmitLock(false);
  };

  useEffect(() => {
    if (shouldCloseModal) {
      // Reset trigger
      if (onModalClosed) onModalClosed();
    }
  }, [shouldCloseModal]);

  useEffect(() => {
    if (!isEditing) {
      resetDefault();
    }
  }, [orderType, side]);

  useEffect(() => {
    if (shouldCloseModal) {
      resetState();
      setIsOrderBoxShow(false);
      getExistingOrder(null);
      resetState();
    }
  }, [selectedSymbol, shouldCloseModal]);

  useEffect(() => {
    resetState();
    resetDefault();
  }, [selectedSymbol, shouldCloseModal]);

  const SLTP_PERCENTAGE = 0.02;

  const livePrice = useSelector((state) => {
    const symbol = isEditing
      ? existingOrder?.symbol?.toUpperCase()
      : selectedSymbol?.toUpperCase();
    return state.market.prices[symbol];
  });

  // console.log(livePrice, "livePrice");

  const selectedSymbolObj = watchlist.find(
    (item) => item.symbol === selectedSymbol
  );
  const instrumentId = selectedSymbolObj?.instrument_id;

  const getCurrentLivePrice = useCallback(() => {
    const symbol = selectedSymbol?.toUpperCase();
    if (!symbol) return 0;
    return side === "BUY" ? livePrices[symbol]?.ask : livePrices[symbol]?.bid;
  }, [selectedSymbol, livePrices, side]);

  useEffect(() => {
    if (existingOrder) {
      setSide(existingOrder.side || "BUY");
      setQuantity(existingOrder.quantity || "");
      setPrice(existingOrder.price || "");
      setOrderType(
        existingOrder.order_type === "stop-loss"
          ? "stop"
          : existingOrder.order_type
      );

      if (existingOrder.take_profit) {
        setEnableTP(true);
        setTP(existingOrder.take_profit);
      }
      if (existingOrder.stop_loss) {
        setEnableSL(true);
        setSL(existingOrder.stop_loss);
      }
    }
  }, [existingOrder]);

  const priceUsed = useMemo(() => {
    return orderType === "market"
      ? getCurrentLivePrice()
      : parseFloat(price || "0");
  }, [orderType, price, getCurrentLivePrice]);

  const qty = useMemo(() => parseFloat(quantity || "0"), [quantity]);
  const lev = useMemo(() => parseFloat(leverage || "1"), [leverage]);

  useEffect(() => {
    if (!selectedSymbol || !selectedSegment || !selectedContractSize) return;

    const symbol = selectedSymbol.toUpperCase();
    const segment = selectedSegment;
    const contractSize = selectedContractSize;

    if (!priceUsed || !qty || !lev || !contractSize) {
      if (total !== "0.0000") setTotal("0.0000");
      if (net !== "0.0000") setNet("0.0000");
      return;
    }

    let totalInQuote = 0;
    let fxRate = 1;

    if (segment === "forex") {
      const base = symbol.slice(0, 3);
      const quote = symbol.slice(3, 6);
      totalInQuote =
        base === "USD"
          ? (contractSize * qty) / lev
          : (contractSize * priceUsed * qty) / lev;

      if (base !== "USD" && quote !== "USD") {
        const direct = `${quote}USD`;
        const inverse = `USD${quote}`;

        if (livePrices?.[direct]?.bid) {
          fxRate = livePrices[direct].bid;
        } else if (livePrices?.[inverse]?.ask) {
          fxRate = 1 / livePrices[inverse].ask;
        } else {
          console.warn("FX conversion rate missing for:", { direct, inverse });
          setTotal("0.0000");
          setNet("0.0000");
          return;
        }
      }
    } else {
      totalInQuote = (priceUsed * qty) / lev;
    }

    const totalUSD = totalInQuote * fxRate;

    // Commission calc
    let feeInQuote = 0;
    let costBasis = priceUsed * contractSize * qty;
    if (segment === "forex" && symbol.slice(0, 3) === "USD") {
      costBasis = contractSize * qty;
    }

    const commissionRate =
      side === "BUY"
        ? parseFloat(buyCommission || "0")
        : parseFloat(sellCommission || "0");

    if (commissionType === "Flat") {
      feeInQuote = commissionRate;
    } else if (commissionType === "Percentage") {
      feeInQuote = (costBasis * commissionRate) / 100;
    }

    const feeInUSD = feeInQuote * fxRate;
    const newTotal = totalUSD.toFixed(4);
    const newNet = (totalUSD + feeInUSD).toFixed(4);

    if (total !== newTotal) setTotal(newTotal);
    if (net !== newNet) setNet(newNet);
  }, [
    priceUsed,
    qty,
    lev,
    selectedSegment,
    selectedSymbol,
    selectedContractSize,
    buyCommission,
    sellCommission,
    commissionType,
    side,
    livePrices,
  ]);

  const calculatePnL = ({
    entryPrice,
    targetPrice,
    quantity,
    side,
    segment,
    symbol,
    contractSize,
  }) => {
    const entry = parseFloat(entryPrice);
    const target = parseFloat(targetPrice);
    const qty = parseFloat(quantity);
    const direction = side?.toLowerCase() === "buy" ? 1 : -1;

    if (!entry || !target || !qty || !contractSize) return 0;

    const symbolUpper = symbol?.toUpperCase();
    let pnlInQuote = 0;

    if (segment === "forex") {
      const baseCurrency = symbolUpper.slice(0, 3);
      const quoteCurrency = symbolUpper.slice(3, 6);

      if (baseCurrency === "USD") {
        // USD is base → quote is result
        pnlInQuote =
          ((target - entry) * contractSize * qty * direction) / target;
      } else {
        // USD is quote or non-USD
        pnlInQuote = (target - entry) * contractSize * qty * direction;
      }

      // Convert to USD using livePrices
      let fxRate = 1;
      const direct = `${quoteCurrency}USD`;
      const inverse = `USD${quoteCurrency}`;
      const live = livePrices; // assume accessible or passed via prop

      if (quoteCurrency !== "USD") {
        if (live[direct]?.bid) {
          fxRate = live[direct].bid;
        } else if (live[inverse]?.ask) {
          fxRate = 1 / live[inverse].ask;
        } else {
          return 0; // No conversion rate found
        }
      }

      return parseFloat((pnlInQuote * fxRate).toFixed(2));
    }

    // Crypto / Equity
    const pnl = direction * (target - entry) * contractSize * qty;
    return parseFloat(pnl.toFixed(2));
  };

  useEffect(() => {
    if (isEditing) return;
    const qty = parseFloat(quantity);
    const entryPrice =
      orderType === "market" ? getCurrentLivePrice() : parseFloat(price);

    if (!qty || !entryPrice) {
      setEstimatedLoss(null);
      setEstimatedProfit(null);
      return;
    }

    const baseParams = {
      entryPrice,
      quantity: qty,
      segment: selectedSegment,
      symbol: selectedSymbol?.toUpperCase(),
      side,
      contractSize: selectedContractSize,
    };

    if (enableSL && parseFloat(sl)) {
      const pnl = calculatePnL({
        ...baseParams,
        targetPrice: parseFloat(sl),
      });
      setEstimatedLoss(pnl.toFixed(2));
    } else {
      setEstimatedLoss(null);
    }

    if (enableTP && parseFloat(tp)) {
      const pnl = calculatePnL({
        ...baseParams,
        targetPrice: parseFloat(tp),
      });
      setEstimatedProfit(pnl.toFixed(2));
    } else {
      setEstimatedProfit(null);
    }
  }, [sl, tp, quantity, price, enableSL, enableTP, orderType, side, livePrice]);
  useEffect(() => {
    if (!isEditing || !existingOrder) return;

    const qty = parseFloat(existingOrder.quantity);
    const entryPrice = parseFloat(existingOrder.entry_price);

    if (
      !qty ||
      !entryPrice ||
      !selectedContractSize ||
      !selectedSegment ||
      !selectedSymbol
    ) {
      setEstimatedLoss(null);
      setEstimatedProfit(null);
      return;
    }

    const baseParams = {
      entryPrice,
      quantity: qty,
      segment: existingOrder.segment,
      symbol: existingOrder.symbol?.toUpperCase(),
      side: existingOrder.side?.toUpperCase() === "LONG" ? "BUY" : "SELL",
      contractSize: selectedContractSize,
    };

    if (enableSL && parseFloat(sl)) {
      const pnl = calculatePnL({
        ...baseParams,
        targetPrice: parseFloat(sl),
      });
      setEstimatedLoss(pnl.toFixed(2));
    } else {
      setEstimatedLoss(null);
    }

    if (enableTP && parseFloat(tp)) {
      const pnl = calculatePnL({
        ...baseParams,
        targetPrice: parseFloat(tp),
      });
      setEstimatedProfit(pnl.toFixed(2));
    } else {
      setEstimatedProfit(null);
    }
  }, [sl, tp, enableSL, enableTP, isEditing, existingOrder, livePrice]);

  useEffect(() => {
    if (orderType === "market") return;
    if (!priceInitializedRef.current) {
      const current = getCurrentLivePrice();
      setPrice(current);
      priceInitializedRef.current = true;
    }
  }, [orderType, livePrice, side]);

  useEffect(() => {
    if (orderType === "limit" || orderType === "stop") {
      priceInitializedRef.current = false;
    }
  }, [orderType, side]);

  const isValid = () => {
    if (!isEditing && (!quantity || parseFloat(quantity) <= 0)) return false;
    if (!isEditing && ["limit", "stop"].includes(orderType)) {
      if (!price || parseFloat(price) <= 0 || priceError) return false;
    }
    if (enableSL && (!sl || slError)) return false;
    if (enableTP && (!tp || tpError)) return false;
    if (minQtyError) return false;
    return true;
  };

  // useEffect(() => {
  //   if (selectedSegment === "crypto") {
  //     const priceUsed =
  //       orderType === "market"
  //         ? getCurrentLivePrice()
  //         : parseFloat(price || "0");
  //     const qty = parseFloat(quantity || "0");

  //     if (priceUsed && qty) {
  //       const minQty = 6 / priceUsed;
  //       const allowedDecimals = stepSize.toString().split(".")[1]?.length || 0;
  //       const minQtyFixed = minQty.toFixed(allowedDecimals);
  //       console.log(minQtyFixed, "minQtyFixed");
  //       if (qty < minQtyFixed) {
  //         setMinQtyError(`Minimum quantity should be at least ${minQtyFixed}`);
  //       } else {
  //         setMinQtyError("");
  //       }
  //     } else {
  //       setMinQtyError("");
  //     }
  //   } else {
  //     setMinQtyError("");
  //   }
  // }, [selectedSegment, price, quantity]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submitLock || !isValid() || priceError) return;
    setSubmitLock(true);

    const basePayload = {
      user_id: userData?.id,
      broker_id: userData?.refred_by_id,
      instrument_id: instrumentId,
      stop_loss: enableSL ? parseFloat(sl) : null,
      take_profit: enableTP ? parseFloat(tp) : null,
    };

    const payload = {
      ...basePayload,
      side,
      order_type: orderType === "stop" ? "stop-loss" : orderType,
      price,
      quantity: parseFloat(quantity),
      leverage: 1,
    };

    const res =
      orderType === "market"
        ? await createMarketOrder(payload)
        : await createOrder(payload);

    if (res?.data?.success) {
      resetDefault();
      // toast.success("Order placed successfully", {
      //   position: "top-right",
      // });
    } else {
      resetDefault();
      toast.error(res?.error?.data?.message || "Failed to place order", {
        position: "top-right",
      });
    }
  };

  const handleModify = async (e) => {
    e.preventDefault();
    if (!isEditing || !existingOrder) return;

    const entryPrice = parseFloat(existingOrder.entry_price);
    const currentBid = parseFloat(livePrice?.bid || 0);
    const currentAsk = parseFloat(livePrice?.ask || 0);

    // Validate TP
    if (enableTP && tp) {
      const error = validateSLTP({
        type: "tp",
        value: tp,
        entryPrice,
        side,
        livePrice,
      });
      if (error) {
        setTpError(error);
        toast.error(error);
        return;
      }
    }

    // Validate SL
    if (enableSL && sl) {
      const error = validateSLTP({
        type: "sl",
        value: sl,
        entryPrice,
        side,
        livePrice,
      });
      if (error) {
        setSlError(error);
        toast.error(error);
        return;
      }
    }

    // ✅ Robust fallback logic (preserve or nullify as intended)
    let stop_loss;
    if (enableSL && sl !== "") {
      stop_loss = parseFloat(sl);
    } else if (!enableSL) {
      stop_loss = null; // ❌ Don't preserve old value — user disabled it
    } else {
      stop_loss = undefined; // untouched (won’t be sent)
    }

    let take_profit;
    if (enableTP && tp !== "") {
      take_profit = parseFloat(tp);
    } else if (!enableTP) {
      take_profit = null; // ❌ clear it intentionally
    } else {
      take_profit = undefined;
    }

    const payload = {
      user_id: userData?.id,
      broker_id: userData?.refred_by_id,
      instrument_id: instrumentId,
      order_id: existingOrder.id,
      ...(stop_loss !== undefined && { stop_loss }),
      ...(take_profit !== undefined && { take_profit }),
    };

    const res = await modifyOrder(payload);

    if (res?.data?.success) {
      resetState();
      setIsOrderBoxShow(false);
      getExistingOrder(null);
      // toast.success("SL/TP updated successfully");
    } else {
      toast.error(res?.error?.data?.message || "Failed to update SL/TP");
    }
  };

  const isValidPriceWithinRange = (price, currentPrice, percentage = 0.2) => {
    const priceFloat = parseFloat(price);
    const min = currentPrice * (1 - percentage);
    const max = currentPrice * (1 + percentage);
    if (priceFloat < min || priceFloat > max) {
      return {
        valid: false,
        message: `Price must be between ${min.toFixed(priceStep)} and ${
          orderType === "market" ? max.toFixed(priceStep) : "current price"
        }`,
      };
    }
    return { valid: true, message: "" };
  };

  const getDecimalPlaces = (stepSize) => {
    if (!stepSize) return 8;
    const decimal = stepSize.toString().split(".")[1];
    return selectedSegment === "equity" ? 2 : stepSize ? stepSize : 0;
  };

  useEffect(() => {
    if (isEditing || !price || isNaN(price)) return;

    const basePrice =
      orderType === "market" ? getCurrentLivePrice() : parseFloat(price || 0);

    if (enableTP) {
      const tpVal =
        side === "BUY"
          ? (basePrice * (1 + SLTP_PERCENTAGE)).toFixed(priceStep)
          : (basePrice * (1 - SLTP_PERCENTAGE)).toFixed(priceStep);
      setTP(tpVal);
    } else {
      setTP("");
    }
  }, [enableTP, price, orderType, side]);

  // Update SL only when enableSL changes or relevant dependencies change
  useEffect(() => {
    if (isEditing || !price || isNaN(price)) return;

    const basePrice =
      orderType === "market" ? getCurrentLivePrice() : parseFloat(price || 0);

    if (enableSL) {
      const slVal =
        side === "BUY"
          ? (basePrice * (1 - SLTP_PERCENTAGE)).toFixed(priceStep)
          : (basePrice * (1 + SLTP_PERCENTAGE)).toFixed(priceStep);
      setSL(slVal);
    } else {
      setSL("");
    }
  }, [enableSL, price, orderType, side]);

  useEffect(() => {
    if (orderType !== "limit") {
      setPriceError("");
      return;
    }

    const priceNum = parseFloat(price);
    const currentAsk = parseFloat(livePrice?.ask || 0);
    const currentBid = parseFloat(livePrice?.bid || 0);

    if (side === "BUY" && priceNum > currentAsk) {
      setPriceError("BUY Limit price must be ≤ current price");
    } else if (side === "SELL" && priceNum < currentBid) {
      setPriceError("SELL Limit price must be ≥ current price");
    } else {
      if (!priceError) {
        setPriceError("");
      }
    }
  }, [price, orderType, side, livePrice, selectedSegment]);

  // if (!isOrderBoxShow) return null;

  return (
    <div className="dash-grid-two">
      <div className="ne_box h_100 p-3 bg2 border_box d_new_right_2">
        <div className="d-flex mb-2">
          <h6 className="text-left drag-handle mr-auto">
            {isEditing ? "Modify Order" : "Trading"}
          </h6>
          {isEditing && (
            <i
              onClick={() => {
                setIsOrderBoxShow(false);
                getExistingOrder(null);
                resetState();
              }}
              className="bi bi-x-lg cursor-pointer"
            />
          )}
        </div>

        {!isEditing && (
          <>
            <ul className="exchange_tab2 drag-handle br10 d-flex mb-3 position-relative">
              <li
                className={
                  side === "SELL" ? "drag-cancel my_color_sell" : "drag-cancel"
                }
                onClick={() => setSide("SELL")}
              >
                <span className="d-block fw600 mb-1">Sell</span>
                {formatDecimal(livePrice?.bid)}
              </li>
              <li
                className={
                  side === "BUY" ? "drag-cancel my_color_buy" : "drag-cancel"
                }
                onClick={() => setSide("BUY")}
              >
                <span className="d-block fw600 mb-1">Buy</span>
                {formatDecimal(livePrice?.ask)}
              </li>
            </ul>

            <div className="position_tab position_tab_center d-flex  ">
              {["market", "limit"].map((type) => (
                <a
                  key={type}
                  href="#!"
                  onClick={() => setOrderType(type)}
                  className={orderType === type ? "active" : ""}
                >
                  {type.charAt(0)?.toUpperCase() + type.slice(1)}
                </a>
              ))}
            </div>
          </>
        )}

        <form
          className="h100"
          onSubmit={isEditing ? handleModify : handleSubmit}
          autoComplete="off"
          // style={{ position: "relative", overflow: "visible", zIndex: 2 }}
        >
          {isEditing ? (
            <div className="orderforms-inputs ">
              <div className="field f-cb mb-3">
                <label>Take Profit:</label>
                <input
                  type="text"
                  value={tp}
                  placeholder="Enter TP"
                  onChange={(e) => {
                    const val = e.target.value;

                    if (val === "") {
                      setTP(null); // Set TP to null (for backend update)
                      setTpError(null); // Clear any existing error
                      setEnableTP(false); // Optional: disable TP toggle if needed
                      return; // Exit early
                    }

                    // 2. Enforce max decimals using priceStep
                    const maxDecimals = getDecimalPlaces(priceStep);
                    const parts = val.split(".");
                    if (parts.length > 1 && parts[1].length > maxDecimals)
                      return;

                    setEnableTP(true);
                    setTP(val);

                    const error = validateSLTP({
                      type: "tp",
                      value: val,
                      entryPrice: existingOrder?.entry_price,
                      side,
                      livePrice,
                    });

                    setTpError(error);
                  }}
                />
                {tpError && (
                  <span style={{ color: "rgb(242, 54, 69)" }}>{tpError}</span>
                )}
                {estimatedProfit !== null && !tpError && (
                  <div
                    className={`small mt-1 ${
                      parseFloat(estimatedProfit) < 0
                        ? "text-danger"
                        : "text-success"
                    }`}
                    style={{ fontSize: "10px", fontWeight: "500" }}
                  >
                    Est. Profit: {formatDecimal(estimatedProfit)} {currency}
                  </div>
                )}
              </div>

              <div className="field f-cb mb-3">
                <label>Stop Loss:</label>
                <input
                  type="text"
                  value={sl}
                  placeholder="Enter SL"
                  onChange={(e) => {
                    const val = e.target.value;

                    if (val === "") {
                      setSL(null); // This tells your system to remove SL
                      setSlError(null); // Clear any existing error
                      setEnableSL(false); // Optional: disable SL toggle if applicable
                      return;
                    }

                    const maxDecimals = getDecimalPlaces(priceStep);
                    const parts = val.split(".");
                    if (parts.length > 1 && parts[1].length > maxDecimals)
                      return;

                    setEnableSL(true);
                    setSL(val);

                    if (!isEditing) return;

                    const error = validateSLTP({
                      type: "sl",
                      value: val,
                      entryPrice: existingOrder?.entry_price,
                      side,
                      livePrice,
                    });

                    setSlError(error);
                  }}
                />
                {slError && (
                  <span style={{ color: "rgb(242, 54, 69)" }}>{slError}</span>
                )}
                {estimatedLoss !== null && !slError && (
                  <div
                    className={`small mt-1 ${
                      parseFloat(estimatedLoss) < 0
                        ? "text-danger"
                        : "text-success"
                    }`}
                    style={{ fontSize: "10px", fontWeight: "500" }}
                  >
                    Est. Loss: {formatDecimal(estimatedLoss)} {currency}
                  </div>
                )}
              </div>

              <button
                className={`btn w100 my_color_buy ${
                  (tpError || slError) && "btn-disabled"
                }`}
                type="submit"
                disabled={slError || tpError}
              >
                <div>
                  <span>Update SL/TP</span>
                  {/* <span className="btn_small d-block">
                    {selectedSymbol?.toUpperCase()} MODIFY
                  </span> */}
                </div>
              </button>
            </div>
          ) : (
            <>
              <div className="orderforms-inputs orderforms_sc pt-3">
                <div className="box-qty">
                  <div className="field f-cb mb-2">
                    <label>Quantity</label>
                    <div className="iptwrap">
                      <QuantityInput
                        segment={selectedSegment}
                        stepSize={priceStep}
                        minValue={stepSize}
                        value={quantity}
                        onChange={setQuantity}
                        setHasError={setHasError}
                        tabChanging={`${side}-${orderType}-${selectedSymbol}`}
                      />
                      {!hasError && minQtyError && (
                        <span
                          className="d-flex"
                          style={{ color: "#e40058", marginTop: "2px" }}
                        >
                          {minQtyError}
                        </span>
                      )}
                    </div>
                  </div>

                  {orderType !== "market" && (
                    <div className="field f-cb mb-2">
                      <label>Price</label>
                      <div className="iptwrap">
                        <input
                          type="text"
                          step="any"
                          value={price}
                          style={{
                            border: priceError ? "2px solid #e40058" : "",
                            borderRadius: "4px",
                          }}
                          onChange={(e) => {
                            const val = e.target.value;
                            setPrice(val);

                            const { valid, message } = isValidNumericInput(val);
                            if (!valid) {
                              setPriceError(message);
                              return;
                            }

                            const maxDecimals = getDecimalPlaces(priceStep);
                            const parts = val.split(".");
                            if (parts[1] && parts[1].length > maxDecimals) {
                              setPriceError(
                                `Max ${maxDecimals} decimal places allowed.`
                              );
                              return;
                            }

                            // ✅ Also check price within ±2%
                            if (["limit", "stop"].includes(orderType)) {
                              const current = getCurrentLivePrice();
                              const rangeCheck = isValidPriceWithinRange(
                                val,
                                current
                              );
                              setPriceError(
                                rangeCheck.valid ? "" : rangeCheck.message
                              );
                            } else {
                              setPriceError("");
                            }
                          }}
                        />
                        {priceError && (
                          <div
                            style={{
                              // color: "#ffd700",
                              background: "#333",
                              padding: "6px 8px",
                              borderRadius: "4px",
                              marginBottom: "4px",
                              fontSize: "12px",
                              position: "absolute",
                              top: "-43px",
                              minHeight: "40px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "100%",
                              textAlign: "center",
                            }}
                          >
                            {priceError}
                          </div>
                        )}
                        {currency !== "USD" && (
                          <span className="mt-2 d-flex">
                            Converted value of {currency}:-{" "}
                            {formatDecimal(price)}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="row pt-3 mb-3 align-items-end m-0">
                    <div className="col-5 p-0">
                      <div
                        className="form-check mb-2"
                        onClick={() => {
                          const checked = !enableTP;
                          setEnableTP(checked);
                          if (checked && (livePrice || price)) {
                            const basePrice =
                              orderType === "market"
                                ? getCurrentLivePrice()
                                : parseFloat(price || 0);

                            const val =
                              side === "BUY"
                                ? (basePrice * (1 + SLTP_PERCENTAGE)).toFixed(
                                    priceStep
                                  )
                                : (basePrice * (1 - SLTP_PERCENTAGE)).toFixed(
                                    priceStep
                                  );
                            if (tpError) {
                              setTpError("");
                            }

                            setTP(val);
                          } else {
                            setTP("");
                            setTpError("");
                          }
                        }}
                      >
                        <input
                          className="form-check-input check_box"
                          type="checkbox"
                          checked={enableTP}
                          readOnly
                        />
                        <span className="checkmark" />
                        <label className="form-check-label pl-2">
                          Take Profit
                        </label>
                      </div>
                      {/* <input
                    type="text"
                    placeholder={0}
                    disabled={!enableTP}
                    value={tp}
                    onChange={(e) => setTP(e.target.value)}
                  /> */}
                      <input
                        type="text"
                        placeholder={0}
                        disabled={!enableTP}
                        value={tp}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (tpError) {
                            setTpError("");
                          }
                          const maxDecimals = getDecimalPlaces(priceStep);
                          const parts = val.split(".");
                          if (parts.length > 1 && parts[1].length > maxDecimals)
                            return;
                          setTP(val);
                          const { valid, message } = isValidNumericInput(val);
                          setTpError(valid ? "" : message);
                        }}
                      />
                      {estimatedProfit !== null && (
                        <div className="text-success small mt-1">
                          Est. Profit: {formatDecimal(estimatedProfit)}
                          {currency}
                        </div>
                      )}
                      {tpError && (
                        <div
                          style={{
                            // color: "#ffd700",
                            background: "#333",
                            padding: "6px 8px",
                            borderRadius: "4px",
                            marginBottom: "4px",
                            fontSize: "12px",
                            position: "absolute",
                            top: "-43px",
                            minHeight: "40px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                            textAlign: "center",
                          }}
                        >
                          {tpError}
                        </div>
                      )}
                    </div>

                    <div className="col-2 pl-2" />

                    <div className="col-5 p-0">
                      <div
                        className="form-check mb-2"
                        onClick={() => {
                          const checked = !enableSL;
                          setEnableSL(checked);
                          if (checked && (livePrice || price)) {
                            const basePrice =
                              orderType === "market"
                                ? getCurrentLivePrice()
                                : parseFloat(price || 0);

                            const val =
                              side === "BUY"
                                ? (basePrice * (1 - SLTP_PERCENTAGE)).toFixed(
                                    priceStep
                                  )
                                : (basePrice * (1 + SLTP_PERCENTAGE)).toFixed(
                                    priceStep
                                  );
                            if (slError) {
                              setSlError("");
                            }

                            setSL(val);
                          } else {
                            setSL("");
                            setSlError("");
                          }
                        }}
                      >
                        <input
                          className="form-check-input check_box"
                          type="checkbox"
                          checked={enableSL}
                          readOnly
                        />
                        <span className="checkmark" />
                        <label className="form-check-label pl-2">
                          Stop Loss
                        </label>
                      </div>
                      {/* <input
                    type="text"
                    placeholder={0}
                    disabled={!enableSL}
                    value={sl}
                    onChange={(e) => setSL(e.target.value)}
                  /> */}
                      <input
                        type="text"
                        placeholder={0}
                        disabled={!enableSL}
                        value={sl}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (slError) {
                            setSlError("");
                          }
                          const maxDecimals = getDecimalPlaces(priceStep);
                          const parts = val.split(".");
                          if (parts.length > 1 && parts[1].length > maxDecimals)
                            return;
                          setSL(val);
                          const { valid, message } = isValidNumericInput(val);
                          setSlError(valid ? "" : message);
                        }}
                      />
                      {estimatedLoss !== null && (
                        <div className="text-danger small mt-1">
                          Est. Loss:{formatDecimal(estimatedLoss)} {currency}
                        </div>
                      )}
                      {slError && (
                        <div
                          style={{
                            // color: "#ffd700",
                            background: "#333",
                            padding: "6px 8px",
                            borderRadius: "4px",
                            marginBottom: "4px",
                            fontSize: "12px",
                            position: "absolute",
                            top: "-43px",
                            minHeight: "40px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                            textAlign: "center",
                          }}
                        >
                          {slError}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="field f-cb mb-2">
                    <label>Total</label>
                    <div className="iptwrap">
                      <input type="text" value={total} disabled />
                    </div>
                  </div>

                  <div
                    className="d-flex justify-content-between"
                    style={{ fontSize: 10 }}
                  >
                    <p>
                      Trade Fee: {buyCommission}
                      {commissionType === "Flat" ? "" : "%"}
                    </p>
                    <p>Net Amount: {net}</p>
                  </div>
                </div>
              </div>
              <button
                className={`btn floating-btn w100 ${
                  (submitLock || !isValid() || hasError) && "btn-disabled"
                } ${side === "BUY" ? "my_color_buy" : "my_color_sell"}`}
                type="submit"
                disabled={submitLock || !isValid() || hasError}
              >
                <div>
                  <span>{side}</span>
                  <span className="btn_small d-block">
                    {selectedSymbol?.toUpperCase()} {orderType.toUpperCase()}
                  </span>
                </div>
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default TradeBox;
